/**
 * Client-side expiry for session refresh timing. The API validates the id_token JWT `exp`
 * claim — not Microsoft `expires_in` (access token lifetime). Using only `expires_in` for
 * `expiration_date` causes refresh to run too late and users get 401 → full login.
 */

export function parseJwtPayload(token) {
  if (!token || typeof token !== 'string') {
    return null;
  }
  const parts = token.split('.');
  if (parts.length < 2) {
    return null;
  }
  let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) {
    base64 += '=';
  }
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return JSON.parse(new TextDecoder('utf-8').decode(bytes));
  } catch {
    return null;
  }
}

/**
 * Prefer id_token `exp` (what the server checks). Fall back to expires_in when missing.
 */
export function getExpirationDateFromAuthTokens(idToken, expiresInSeconds) {
  const payload = parseJwtPayload(idToken);
  if (payload && typeof payload.exp === 'number') {
    return new Date(payload.exp * 1000);
  }
  if (typeof expiresInSeconds === 'number' && Number.isFinite(expiresInSeconds)) {
    return new Date(Date.now() + expiresInSeconds * 1000);
  }
  return new Date(Date.now() + 3600 * 1000);
}

/** Optional: set REACT_APP_SESSION_EXPIRY_CAP_SECONDS in client env to shorten session for local experiments. */
export function applyOptionalSessionExpiryCap(expiryDate) {
  const raw = process.env.REACT_APP_SESSION_EXPIRY_CAP_SECONDS;
  if (!raw) {
    return expiryDate;
  }
  const capSec = parseInt(raw, 10);
  if (!Number.isFinite(capSec) || capSec <= 0) {
    return expiryDate;
  }
  const capEnd = Date.now() + capSec * 1000;
  return new Date(Math.min(expiryDate.getTime(), capEnd));
}

/**
 * Seconds until the id_token JWT `exp` (what the API validates). Used for refresh timing only.
 * Do not use `expiration_date` here — it may be shortened by applyOptionalSessionExpiryCap while
 * the id_token is still valid for much longer, which would otherwise trigger refresh on every poll.
 */
export function getSecondsUntilIdTokenExpiry() {
  const idToken = localStorage.getItem('id_token');
  const payload = parseJwtPayload(idToken);
  if (payload && typeof payload.exp === 'number') {
    return payload.exp - Math.floor(Date.now() / 1000);
  }
  return Number.POSITIVE_INFINITY;
}
